
import React from "react";
import { i18nManager } from "../i18n";
import { useSearchParams } from "react-router-dom";
import { Utils, Schemes, LzEncryptor } from "../utils";
import { ApiResponseData, ILog, Level, MnemosyneService, SanitizedResponse } from "../services";
import {
    Button, FormControl, Scheme, Table, TextInput, InputManageRef, ButtonManageRef,
    Popover, PopoverManageRef, Checkbox, Alignment, NoseurObject, Timer, DateTimePicker, Chart,
    DateTimePickerManageRef, ComponentRenderType, DateTimePickerSelectionMode, ChartType, ChartData,
    DataManageRef, Column, ScrollPanel, ChartManageRef, DOMHelper, ViewportSensor, Orientation, ViewportSensorManageRef, alertDialog, AlertInterface, TextAreaInput, Transition,
} from "@ronuse/noseur";

function Dashboard() {
    const labels = i18nManager.Labels.dashboard;
    const [page, setPage] = React.useState<number>(0);
    const mnemosyneService = MnemosyneService.getInstance();
    const tableManageRef = React.useRef<DataManageRef>(null);
    const scrollPanelRef = React.useRef<HTMLDivElement>(null);
    const dateFilterSpan = React.useRef<HTMLSpanElement>(null);
    const settingButton = React.useRef<HTMLButtonElement>(null);
    const [timer, setTimer] = React.useState<Timer | null>(null);
    const logChartManageRef = React.useRef<ChartManageRef>(null);
    const searchInputManageRef = React.useRef<InputManageRef>(null);
    const queryButtonManageRef = React.useRef<ButtonManageRef>(null);
    const [logs, setLogs] = React.useState<ApiResponseData<ILog>>({});
    const settingsPopOverManageRef = React.useRef<PopoverManageRef>(null);
    const dateFilterManageRef = React.useRef<DateTimePickerManageRef>(null);
    const [chartData, setChartData] = React.useState<ChartData>({} as ChartData);
    const [logDetailDialog, setLogDetailDialog] = React.useState<AlertInterface>();
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const latestSpinnerViewportSensorManageRef = React.useRef<ViewportSensorManageRef>(null);
    const [liveTail, setLiveTail] = React.useState<boolean>((searchParams.get("live_tail") ?? "false") === "true");
    const [showGraph, setShowGraph] = React.useState<boolean>((searchParams.get("show_graph") ?? "false") === "true");
    const footerChartData = Array(3).fill(0).map((_, __) => Array(60).fill(0).map((_) => Utils.randomNumber(0, 550)));
    const size = parseInt(searchParams.get("size") ?? "1000");

    React.useEffect(() => {
        if (searchParams.has("group")) {
            const currentQuery = LzEncryptor.decrypt(searchParams.get("query") ?? "", true);
            const query = (currentQuery ? (currentQuery + " AND ") : "") + "group CONTAINS " + searchParams.get("group");
            searchParams.set("query", LzEncryptor.encrypt(query, true));
            searchParams.delete("group");
        }
    }, []);
    React.useEffect(() => {
        Utils.updateSearchParams("tels", 0, setSearchParams);
        if (liveTail) {
            let theTimer = timer;
            if (theTimer == null) {
                theTimer = new Timer({
                    delay: 2000,
                    isInterval: true,
                    timeout: 9999999999999,
                    cbs: {
                        onEnd: () => toggleLiveTail(false),
                        onAction: (_: any) => queryLog(undefined, 1, true),
                    }
                });
                setTimer(theTimer);
            }
            theTimer?.start();
            return () => theTimer?.stop();
        } else {
            timer?.stop();
        }
    }, [liveTail, timer]);

    function buildLogLevel(level: Level) {
        let color = "#6B6B6B";
        if (level === Level.INFO) color = "#001F8D";
        if (level === Level.WARN) color = "#FFB800";
        if (level === Level.TRACE) color = "#436CFF";
        if (level === Level.ERROR) color = "#FF0000";
        if (level === Level.FATAL || level === Level.PANIC) color = "#000000";
        return (<span style={{ color, fontWeight: "bold" }}>{level}</span>);
    }

    return (<ScrollPanel className="dashboard" scheme={Schemes.RIVTN_MNEMOSYNE}>
        <div className="header">
            <span className="title">{i18nManager.Labels.display_name}</span>
            <FormControl rightContent={<Button scheme={Scheme.SECONDARY} leftIcon="fa fa-times" onClick={clearSearchInput} />} className="search-control" borderless>
                <TextInput defaultValue={searchParams.has("query") ? LzEncryptor.decrypt(searchParams.get("query"), true) : ""}
                    manageRef={searchInputManageRef} fill />
            </FormControl>
            <Button manageRef={queryButtonManageRef} text={labels.query_logs} scheme={Schemes.RIVTN_MNEMOSYNE} style={{ backgroundColor: "white", width: "fit-content" }}
                onClick={() => queryLog()} loadingProps={Utils.BUTTON_LOADING_PROPS} outlined />
            <Button leftIcon="fa fa-download" scheme={Scheme.LIGHT} style={{ fontSize: 20 }}
                loadingProps={Utils.BUTTON_LOADING_PROPS_ICON_ONLY} onClick={() => Utils.exportLogs(logs.content ?? [], labels.table)} textOnly />
            <Button leftIcon="fa fa-gear" ref={settingButton} scheme={Scheme.LIGHT} style={{ fontSize: 20 }}
                onClick={(e: any) => settingsPopOverManageRef.current?.toggle(e, settingButton.current!)} textOnly />
        </div>
        <div ref={scrollPanelRef} className="container" style={{ overflow: "auto" }}>
            {showGraph ? (<div className="graph">
                <Chart manageRef={logChartManageRef} type={ChartType.LINE} data={chartData as ChartData} className="card" style={{ zIndex: 1 }}
                    canvasStyle={{ height: "55px", width: "100%" }} id={DOMHelper.uniqueElementId("chart")} />
            </div>) : null}
            <ViewportSensor manageRef={latestSpinnerViewportSensorManageRef} scrollContainerRef={scrollPanelRef} orientation={Orientation.VERTICAL} onEnterViewport={() => {
                queryLog(undefined, page + 1, true);
                return liveTail;
            }} onExitViewport={() => false}>
                <i className="loader fas fa-spinner fa-pulse" />
            </ViewportSensor>
            <Table className="table" data={logs.content} totalRecords={logs.total_elements} dataRefreshKeys={["id", "content"]} manageRef={tableManageRef}
                rowsPerPage={99999999999999999999999999} style={{ marginTop: 10 }} onRowSelection={(log: ILog) => {
                    showLogDetail(log);
                    return true;
                }} stripedRows>
                <Column dataKey="created_at" header={labels.table.timestamp} style={{ width: "210px" }} />
                <Column dataKey="level" header={labels.table.level} style={{ maxWidth: "25px" }} template={(level: Level) => buildLogLevel(level)}/>
                <Column dataKey="group" header={labels.table.group} />
                <Column dataKey="trace_id" header={labels.table.trace_id} style={{ maxWidth: "30px" }} />
                <Column dataKey="content" header={labels.table.content} style={{ maxWidth: "250px" }} />
            </Table>
            {!logs.empty ? (<ViewportSensor className="loader" scrollContainerRef={scrollPanelRef} orientation={Orientation.VERTICAL} onEnterViewport={() => {
                if (logs.content?.length) queryLog(undefined, page + 1);
                return true;
            }}>
                <i className="fas fa-spinner fa-pulse" />
            </ViewportSensor>) : null}
        </div>

        <Popover manageRef={settingsPopOverManageRef} className="settings-popover">
            <span ref={dateFilterSpan}
                onClick={(e: any) => dateFilterManageRef.current?.toggle(e, dateFilterSpan.current as any)}>{labels.settings.date_filter} <i className="fa fa-filter" /></span>
            <span><Checkbox checked={liveTail} label={labels.settings.live_tail} alignLabel={Alignment.LEFT}
                onChange={(e: any) => toggleLiveTail(e.checked)} scheme={Schemes.RIVTN_MNEMOSYNE} /></span>
            <span><Checkbox checked={showGraph} label={labels.settings.show_graph} alignLabel={Alignment.LEFT}
                onChange={(e: any) => toggleShowGraph(e.checked)} scheme={Schemes.RIVTN_MNEMOSYNE} /></span>
        </Popover>
        <DateTimePicker scheme={Schemes.RIVTN_MNEMOSYNE} type={ComponentRenderType.POPOVER} sticky selectionMode={DateTimePickerSelectionMode.RANGE} manageRef={dateFilterManageRef} />
    </ScrollPanel>);

    function clearSearchInput() {
        searchInputManageRef.current?.clear();
        queryLog("");
    }

    function toggleLiveTail(checked: boolean) {
        setLiveTail(checked);
        if (!checked) setTimer(null);
        Utils.updateSearchParams("live_tail", (checked ? "true" : undefined), setSearchParams);
        if (checked) latestSpinnerViewportSensorManageRef.current?.mountChildren();
        else latestSpinnerViewportSensorManageRef.current?.unmountChildren();
    }

    function toggleShowGraph(checked: boolean) {
        setShowGraph(checked);
        if (checked) loadChartData();
        Utils.updateSearchParams("show_graph", (checked ? "true" : undefined), setSearchParams);
    }

    function queryLog(queryString?: string, page: number = 1, latest?: boolean) {
        if (latest) page = 1;
        const query: NoseurObject<any> = {};
        const value = (queryString ?? searchInputManageRef.current?.value() ?? "");
        const valueParts = value.toLowerCase().split("and");
        if (!latest) queryButtonManageRef.current?.setLoadingState(!liveTail);
        for (const valuePart of valueParts) {
            let operator;
            let leftOperand;
            let rightOperand;
            let subValueParts: string[] = [];
            if (valuePart.includes(" contains ")) {
                operator = "__like";
                subValueParts = valuePart.trim().split(" contains ");
            } else if (valuePart.includes(" eq ") || valuePart.includes(" = ") || valuePart.includes(" equals ")) {
                operator = "__eq";
                let seperator = " = ";
                if (valuePart.includes(" eq ")) seperator = " eq ";
                if (valuePart.includes(" equals ")) seperator = " equals ";
                subValueParts = valuePart.trim().split(seperator);
            } else {
                operator = "__like";
                subValueParts = ["content", valuePart.trim()];
            }
            leftOperand = subValueParts[0];
            rightOperand = subValueParts.length > 1 ? subValueParts[1] : "";
            if (rightOperand.startsWith("'") || rightOperand.startsWith('"')) rightOperand = rightOperand.substring(1);
            if (rightOperand.endsWith("'") || rightOperand.endsWith('"')) rightOperand = rightOperand.substring(0, rightOperand.length - 1);
            const queryKey = `${leftOperand}${operator}`;
            if (queryKey in query) {
                query[queryKey] = ([] as string[]).concat(query[queryKey]);
                query[queryKey].push(rightOperand);
            } else {
                query[queryKey] = rightOperand;
            }
        }
        const previousTotalElements = parseInt(searchParams.get("tels") ?? "0") ?? logs.total_elements ?? 0;
        Utils.updateSearchParams("query", (value ? LzEncryptor.encrypt(value, true) : undefined), setSearchParams);
        loadChartData();
        mnemosyneService.queryLogs({ ...query, page, size, sort: "created_at,DESC" }).then(({ apiResponse }: SanitizedResponse<ILog>) => {
            setPage(page);
            const currentTotalElements = apiResponse.data?.total_elements ?? 0;
            let newLogsContent = apiResponse.data?.content;
            const newOldTotalElementsDiffs = currentTotalElements - previousTotalElements;
            if (latest && newOldTotalElementsDiffs > 0 && newOldTotalElementsDiffs < size) {
                newLogsContent = newLogsContent?.slice(0, newOldTotalElementsDiffs);
                apiResponse.data!.content = (newLogsContent ?? []).concat(logs.content ?? []);
            } else {
                apiResponse.data!.content = ((latest && newOldTotalElementsDiffs === 0 ? [] : (logs.content ?? []))).concat(newLogsContent ?? []);
            }
            setLogs(apiResponse.data!);
            Utils.updateSearchParams("tels", apiResponse.data?.total_elements, setSearchParams);
        }).catch(MnemosyneService.reportError).finally(() => {
            tableManageRef.current?.setLoadingState(false);
            queryButtonManageRef.current?.setLoadingState(false);
        });
    }

    async function loadChartData() {
        if (!showGraph) return;
        const newChartData = {
            labels: Array(60).fill(0).map((_, i) => i + 1),
            datasets: footerChartData.map((data, index) => {
                return {
                    data,
                    fill: true,
                    label: "R" + (index + 1),
                    hoverOffset: data.length,
                    backgroundColor: `rgba(${Utils.randomNumber(index, 10)}, ${Utils.randomNumber(index, 90)}, ${Utils.randomNumber(index, 120)}, 0.3)`,
                };
            })
        };
        setChartData(newChartData);
        logChartManageRef.current?.update(ChartType.LINE, newChartData, {});
    }

    async function showLogDetail(log: ILog, maximized: boolean = false) {
        if (logDetailDialog != undefined) {
            logDetailDialog!.destroy();
        }
        const dialog = alertDialog({
            cancel: null,
            confirm: null,
            dismissibleModal: true,
            alignment: Alignment.RIGHT,
            transition: Transition.NONE,
            style: { height: "100%", maxHeight: "100%", maxWidth: "100%" },
            componentProps: { maximized, noOverlay: true, modalProps: { style: {} } },
            message: (<div className="log-detail">
                <div className="main top">
                    <span>{labels.table.content}</span>
                    <span>
                        <i className="fa fa-expand" onClick={() => dialog.destroy(() => showLogDetail(log, !maximized))} />
                        <i className="fa fa-times" onClick={() => dialog.destroy()} />
                    </span>
                </div>
                <TextAreaInput defaultValue={log.content} />
                <span><span className="main">{labels.table.timestamp}:</span>  {log.created_at}</span>
                <span><span className="main">{labels.table.level}:</span>       {buildLogLevel(log.level)}</span>
                <span><span className="main">{labels.table.group}:</span>     {log.group}</span>
                <span><span className="main">{labels.table.trace_id}:</span>    f{log.trace_id}</span>
                <span><span className="main">{labels.table.span_id}:</span>     f{log.span_id}</span>
            </div>),
        });
        dialog.show();
        setLogDetailDialog(dialog);
    }

}

export default Dashboard;
